import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Newspaper, Play } from "lucide-react";
import { Link } from "react-router-dom";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { useLang } from "../i18n/LanguageContext";
import { api } from "../lib/api";
import { SEO } from "../components/SEO";
import { postLoc, isNew } from "../components/NewsSection";
import { JapanMascot } from "../components/JapaneseMotifs";
import { dateLocale } from "../content/i18n_extra";

export default function NewsPage() {
  const { t, lang } = useLang();
  const [posts, setPosts] = useState([]);

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get("/posts").then((r) => setPosts(r.data)).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-mist" data-testid="news-page">
      <SEO
        title="Haberler & Blog"
        description="Japonya'daki ileri kanser tedavilerine dair güncel onaylar, klinik yenilikler ve tedavi portföyümüzdeki gelişmeler."
        path="/haberler"
      />
      <Navbar />
      <div className="pt-28 pb-24 px-6 max-w-6xl mx-auto">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <div className="text-xs font-heading font-bold tracking-[0.3em] uppercase text-brand mb-4 flex items-center gap-2"><Newspaper size={14} /> {t("sec_news_kicker")}</div>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight text-ink text-balance">{t("sec_news_title")}</h1>
          <p className="mt-4 text-lg text-sub leading-relaxed max-w-2xl">{t("sec_news_sub")}</p>
        </motion.div>

        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-5">
          {posts.map((p, i) => (
            <motion.div key={p.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: Math.min(i * 0.06, 0.4) }} data-testid={`news-list-${p.id}`}>
              <Link to={`/haberler/${p.id}`} className="group block bg-white rounded-3xl border border-black/5 overflow-hidden hover:-translate-y-1 hover:shadow-xl hover:shadow-brand/10 transition-[transform,box-shadow] duration-300 h-full">
                {p.image_url ? (
                  <div className="relative h-44 overflow-hidden">
                    <img src={p.image_url} alt={postLoc(p, lang, "title")} loading="lazy" onError={(e) => { e.currentTarget.parentElement.style.display = "none"; }} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                    {p.video_url && <span data-testid={`news-tudi-badge-${p.id}`} className="absolute top-3 left-3 flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-ink/80 backdrop-blur text-white text-[10px] font-bold uppercase tracking-wider"><Play size={11} /> {t("news_tudi_badge")}</span>}
                  </div>
                ) : (
                  <div className="relative h-44 overflow-hidden bg-gradient-to-br from-ice via-mist to-blush flex items-center justify-center" data-testid={`news-mascot-tile-${p.id}`}>
                    <JapanMascot className="w-28 opacity-90 group-hover:scale-105 transition-transform duration-700" />
                  </div>
                )}
                <div className="p-7">
                  <div className="flex items-center gap-2 text-[10px] font-bold tracking-widest uppercase">
                    {p.tag && <span className="px-2.5 py-1 rounded-full bg-brand/10 text-brand">{p.tag}</span>}
                    {isNew(p.created_at) && <span data-testid="news-new-badge" className="px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-700 animate-pulse">{t("news_new")}</span>}
                    <span className="text-sub">{new Date(p.created_at).toLocaleDateString(dateLocale(lang))}</span>
                  </div>
                  <h3 className="mt-3.5 font-heading text-lg font-bold text-ink leading-snug">{postLoc(p, lang, "title")}</h3>
                  <p className="mt-2.5 text-sm text-ink/60 leading-relaxed line-clamp-3">{postLoc(p, lang, "excerpt")}</p>
                  <span className="mt-4 inline-flex items-center gap-1.5 text-[13px] font-bold text-brand group-hover:gap-2.5 transition-[gap] duration-200">
                    {t("news_read_more")} <ArrowRight size={13} />
                  </span>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>

        {posts.length === 0 && (
          <div className="mt-16 text-center" data-testid="news-empty">
            <JapanMascot className="w-32 mx-auto" />
            <p className="mt-4 text-sm text-sub">{t("news_empty")}</p>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
